<template>
  <div class="ca-roles">
    <header class="ca-cabecera">
      <div>
        <h1 class="ca-titulo">Roles y permisos</h1>
        <p class="ca-subtitulo">Define roles para tu empresa y asigna las acciones permitidas en cada submódulo.</p>
      </div>
      <button v-if="puedeCrear" class="ca-boton ca-boton--primario" @click="abrirNuevoRol">
        <i class="bi bi-plus-lg"></i> Nuevo rol
      </button>
    </header>

    <div v-if="cargando" class="ca-info">Cargando...</div>

    <div v-else class="ca-layout">
      <!-- Lista de roles -->
      <aside class="ca-roles__lista">
        <div v-if="!roles.length" class="ca-info ca-info--vacio">No hay roles creados.</div>
        <button
          v-for="r in roles"
          :key="r.id"
          :class="['ca-rol-item', rolSeleccionadoId === r.id && 'ca-rol-item--activo']"
          @click="seleccionarRol(r)"
        >
          <div class="ca-rol-item__nombre">
            <strong>{{ r.nombre }}</strong>
            <span class="ca-codigo">{{ r.codigo }}</span>
          </div>
          <div class="ca-rol-item__meta">
            <span><i class="bi bi-key"></i> {{ r.total_permisos }}</span>
            <span><i class="bi bi-people"></i> {{ r.total_usuarios }}</span>
          </div>
        </button>
      </aside>

      <!-- Detalle: matriz de permisos -->
      <section class="ca-roles__detalle">
        <div v-if="!rolSeleccionado" class="ca-info">Selecciona un rol para ver sus permisos.</div>

        <div v-else>
          <div class="ca-detalle__cabecera">
            <div>
              <h2>{{ rolSeleccionado.nombre }}</h2>
              <p class="ca-subtitulo">{{ rolSeleccionado.descripcion || 'Sin descripción.' }}</p>
            </div>
            <div class="ca-detalle__acciones">
              <button v-if="puedeEditar" class="ca-boton ca-boton--secundario ca-boton--xs" @click="editarRol(rolSeleccionado)"><i class="bi bi-pencil"></i> Editar</button>
              <button v-if="puedeEliminar" class="ca-boton ca-boton--peligro ca-boton--xs" @click="eliminarRol(rolSeleccionado)"><i class="bi bi-trash"></i> Eliminar</button>
            </div>
          </div>

          <div v-if="!modulosCatalogo.length" class="ca-info">Cargando catálogo...</div>
          <div v-else class="ca-matriz">
            <table>
              <thead>
                <tr>
                  <th>Submódulo</th>
                  <th v-for="a in acciones" :key="a" class="ca-th-accion">{{ a }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="m in modulosCatalogo" :key="m.id">
                  <tr class="ca-fila-modulo">
                    <td colspan="5"><strong>{{ m.nombre }}</strong></td>
                  </tr>
                  <tr v-for="s in m.submodulos" :key="s.id">
                    <td class="ca-celda-submodulo">{{ s.nombre }} <code class="ca-ruta">{{ s.ruta }}</code></td>
                    <td v-for="a in acciones" :key="a" class="ca-celda-accion">
                      <input v-if="(s.acciones || acciones).includes(a)" type="checkbox" :checked="tienePermiso(s.id, a)" :disabled="!puedeEditar" @change="alternarPermiso(s.id, a, $event.target.checked)"/>
                      <span v-else class="ca-accion-na">-</span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-if="puedeEditar" class="ca-detalle__guardar">
            <button class="ca-boton ca-boton--primario" @click="guardarPermisos" :disabled="guardando">
              <i class="bi bi-check2"></i> Guardar permisos
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Modal rol -->
    <div v-if="modalRol" class="ca-modal-fondo" @click.self="modalRol = null">
      <div class="ca-modal">
        <h2>{{ modalRol.id ? 'Editar rol' : 'Nuevo rol' }}</h2>
        <label>Código<input v-model="modalRol.codigo" class="ca-input"/></label>
        <label>Nombre<input v-model="modalRol.nombre" class="ca-input"/></label>
        <label>Descripción<textarea v-model="modalRol.descripcion" class="ca-input" rows="2"></textarea></label>
        <label>Descuentos
          <select v-model="modalRol.modo_descuento" class="ca-input">
            <option value="bloqueado">Bloqueado (no puede aplicar descuento)</option>
            <option value="directo">Directo (libre, sin restricción)</option>
            <option value="aprobacion">Requiere aprobación del administrador</option>
          </select>
        </label>
        <label class="ca-check">
          <input v-model="modalRol.activo" type="checkbox"/> Activo
        </label>
        <div class="ca-modal__acciones">
          <button class="ca-boton ca-boton--secundario" @click="modalRol = null">Cancelar</button>
          <button class="ca-boton ca-boton--primario" @click="guardarRol" :disabled="guardando">Guardar</button>
        </div>
      </div>
    </div>

    <!-- Confirmacion -->
    <div v-if="confirmacion" class="ca-modal-fondo" @click.self="confirmacion = null">
      <div class="ca-modal ca-modal--confirm">
        <h2>{{ confirmacion.titulo }}</h2>
        <p>{{ confirmacion.mensaje }}</p>
        <div class="ca-modal__acciones">
          <button class="ca-boton ca-boton--secundario" @click="confirmacion = null">Cancelar</button>
          <button class="ca-boton ca-boton--peligro" @click="ejecutarConfirmacion">Eliminar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import controlAcceso from '@/services/controlAccesoService'
import { notifySuccess, notifyError } from '@/services/notificationService'

const ACCIONES = ['ver', 'crear', 'editar', 'eliminar']

export default {
  name: 'ClientControlAccesoRoles',
  computed: {
    puedeCrear() {
      return this.$store.getters.puedoHacer('/control-acceso/roles', 'crear')
    },
    puedeEditar() {
      return this.$store.getters.puedoHacer('/control-acceso/roles', 'editar')
    },
    puedeEliminar() {
      return this.$store.getters.puedoHacer('/control-acceso/roles', 'eliminar')
    }
  },
  data() {
    return {
      cargando: false,
      guardando: false,
      acciones: ACCIONES,
      roles: [],
      modulosCatalogo: [],
      rolSeleccionado: null,
      rolSeleccionadoId: null,
      permisosRol: new Set(),
      modalRol: null,
      confirmacion: null,
    }
  },
  mounted() { this.cargar() },
  methods: {
    async cargar() {
      this.cargando = true
      try {
        const [resR, resM] = await Promise.all([
          controlAcceso.listarRoles(),
          controlAcceso.listarModulos(),
        ])
        this.roles = resR.data || []
        this.modulosCatalogo = resM.data || []
      } catch (e) {
        notifyError('No se pudieron cargar roles o módulos.')
      } finally { this.cargando = false }
    },
    async seleccionarRol(rol) {
      try {
        const res = await controlAcceso.obtenerRol(rol.id)
        this.rolSeleccionado = res.data
        this.rolSeleccionadoId = rol.id
        this.permisosRol = new Set((res.data?.permisos || []).map(p => `${p.submodulo_id}.${p.accion}`))
      } catch (e) {
        notifyError('No se pudo cargar el detalle del rol.')
      }
    },
    tienePermiso(submoduloId, accion) {
      return this.permisosRol.has(`${submoduloId}.${accion}`)
    },
    alternarPermiso(submoduloId, accion, marcar) {
      const clave = `${submoduloId}.${accion}`
      if (marcar) this.permisosRol.add(clave)
      else        this.permisosRol.delete(clave)
      this.permisosRol = new Set(this.permisosRol)
    },
    async guardarPermisos() {
      if (!this.rolSeleccionadoId) return
      const permisos = [...this.permisosRol].map(c => {
        const [sid, accion] = c.split('.')
        return { submodulo_id: parseInt(sid, 10), accion }
      })
      this.guardando = true
      try {
        await controlAcceso.setPermisosRol(this.rolSeleccionadoId, permisos)
        notifySuccess('Permisos del rol actualizados.')
        await this.cargar()
        await this.seleccionarRol({ id: this.rolSeleccionadoId })
      } catch (e) {
        notifyError(e?.response?.data?.error || 'Error al guardar.')
      } finally { this.guardando = false }
    },
    abrirNuevoRol() {
      this.modalRol = { codigo: '', nombre: '', descripcion: '', activo: true, modo_descuento: 'bloqueado' }
    },
    editarRol(r) {
      this.modalRol = { id: r.id, codigo: r.codigo, nombre: r.nombre, descripcion: r.descripcion, activo: !!Number(r.activo), modo_descuento: r.modo_descuento || 'bloqueado' }
    },
    async guardarRol() {
      const r = this.modalRol
      if (!r.codigo || !r.nombre) { notifyError('Código y nombre son obligatorios.'); return }
      if (r.nombre.trim().toLowerCase() === 'administrador') {
        notifyError('El nombre "Administrador" no está disponible. Ese acceso se otorga desde la ficha del usuario, no como un rol.')
        return
      }
      this.guardando = true
      try {
        if (r.id) await controlAcceso.actualizarRol(r.id, r)
        else      await controlAcceso.crearRol(r)
        notifySuccess('Rol guardado.')
        this.modalRol = null
        await this.cargar()
      } catch (e) {
        notifyError(e?.response?.data?.error || 'Error al guardar el rol.')
      } finally { this.guardando = false }
    },
    eliminarRol(r) {
      this.confirmacion = {
        titulo: 'Eliminar rol',
        mensaje: `Se eliminará el rol "${r.nombre}". Si tiene usuarios asignados, fallará.`,
        accion: async () => {
          try {
            await controlAcceso.eliminarRol(r.id)
            notifySuccess('Rol eliminado.')
            this.rolSeleccionado = null
            this.rolSeleccionadoId = null
            await this.cargar()
          } catch (e) {
            notifyError(e?.response?.data?.error || 'No se pudo eliminar.')
          }
        },
      }
    },
    async ejecutarConfirmacion() {
      const accion = this.confirmacion?.accion
      this.confirmacion = null
      if (accion) await accion()
    },
  },
}
</script>

<style scoped>
.ca-roles { color: var(--text-primary); }
.ca-cabecera { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 18px; gap: 16px; }
.ca-titulo { font-size: 1.4rem; margin: 0; }
.ca-subtitulo { margin: 4px 0 0; color: var(--text-secondary); font-size: 0.9rem; }

.ca-layout { display: grid; grid-template-columns: 280px 1fr; gap: 16px; }
@media (max-width: 768px) { .ca-layout { grid-template-columns: 1fr; } }

.ca-roles__lista { display: flex; flex-direction: column; gap: 6px; }
.ca-rol-item {
  text-align: left;
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.ca-rol-item:hover { background: var(--bg-main); }
.ca-rol-item--activo { border-color: var(--primary-main); background: color-mix(in srgb, var(--primary-main) 10%, transparent); }
.ca-rol-item__nombre { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.ca-rol-item__meta { display: flex; gap: 12px; color: var(--text-secondary); font-size: 0.78rem; margin-top: 4px; }

.ca-roles__detalle {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 12px;
  padding: 16px;
  box-shadow: var(--shadow-sm);
}
.ca-detalle__cabecera { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
.ca-detalle__cabecera h2 { font-size: 1.1rem; margin: 0; }
.ca-detalle__acciones { display: flex; gap: 6px; }
.ca-detalle__guardar { display: flex; justify-content: flex-end; margin-top: 14px; }

.ca-matriz { overflow-x: auto; border: 1px solid var(--border-main); border-radius: 8px; }
.ca-matriz table { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
.ca-matriz th, .ca-matriz td { padding: 6px 10px; border-bottom: 1px solid var(--border-main); text-align: left; }
.ca-matriz th { background: var(--bg-main); font-weight: 600; color: var(--text-secondary); }
.ca-th-accion { text-align: center; width: 80px; text-transform: capitalize; }
.ca-fila-modulo td { background: var(--bg-main); color: var(--text-secondary); padding: 6px 12px; }
.ca-celda-submodulo { padding-left: 20px; }
.ca-celda-accion { text-align: center; }
.ca-accion-na { color: var(--text-muted); }

.ca-codigo {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 6px;
  background: var(--bg-main);
  color: var(--text-secondary);
  font-size: 0.74rem;
  font-family: ui-monospace, monospace;
}
.ca-ruta { color: var(--text-muted); font-size: 0.78rem; margin-left: 6px; }

.ca-info { padding: 14px; color: var(--text-secondary); }
.ca-info--vacio { padding: 8px 4px; font-size: 0.85rem; }

.ca-boton {
  border: 1px solid var(--border-main);
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.88rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ca-boton:hover:not(:disabled) { background: var(--bg-main); }
.ca-boton:disabled { opacity: 0.6; cursor: not-allowed; }
.ca-boton--xs { padding: 4px 8px; font-size: 0.78rem; }
.ca-boton--primario { background: var(--primary-main); border-color: var(--primary-main); color: #fff; }
.ca-boton--primario:hover:not(:disabled) { background: var(--primary-hover); border-color: var(--primary-hover); }
.ca-boton--secundario { background: transparent; }
.ca-boton--peligro { color: var(--accent-danger); border-color: color-mix(in srgb, var(--accent-danger) 40%, transparent); }
.ca-boton--peligro:hover:not(:disabled) { background: color-mix(in srgb, var(--accent-danger) 12%, transparent); }

.ca-modal-fondo { position: fixed; inset: 0; z-index: 2000; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; }
.ca-modal {
  background: var(--secondary-bg-main); color: var(--text-primary);
  border-radius: 12px; padding: 22px; min-width: 420px; max-width: 560px;
  border: 1px solid var(--border-main); box-shadow: var(--shadow-md);
}
.ca-modal h2 { font-size: 1.1rem; margin: 0 0 16px; }
.ca-modal label { display: block; margin-bottom: 10px; font-size: 0.85rem; color: var(--text-secondary); }
.ca-modal__acciones { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }

.ca-input {
  width: 100%;
  background: var(--bg-main);
  color: var(--text-primary);
  border: 1px solid var(--border-main);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.92rem;
  margin-top: 4px;
}
.ca-input:focus { outline: none; border-color: var(--border-focus); }
.ca-check { display: inline-flex; align-items: center; gap: 8px; }
</style>
